import {
  CheckCircle,
  Copy,
  Info,
  ShieldCheck,
  Warning,
} from "@phosphor-icons/react";
import React from "react";
import { GOOGLE_GUIDE_STEPS_IMAGES_MAPPER } from "../../../shared/constants";
import useFancybox from "../../../hooks/useFancyBox";

const RedirectLink = ({ href, text }: { href: string; text: string }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-blue-600 dark:text-blue-400 hover:underline"
  >
    {text}
  </a>
);

const StepImage = ({
  step,
  className,
}: {
  step: keyof typeof GOOGLE_GUIDE_STEPS_IMAGES_MAPPER;
  className?: string;
}) => (
  <div className="w-max mt-3">
    <img
      src={GOOGLE_GUIDE_STEPS_IMAGES_MAPPER[step].link}
      alt="Google Play Store"
      data-fancybox="google-guide"
      data-src={GOOGLE_GUIDE_STEPS_IMAGES_MAPPER[step].link}
      className={`max-w-[900px] w-full h-auto border border-gray-200 dark:border-neutral-700 cursor-zoom-in rounded-xl shadow-sm my-2 ${className ?? ""}`}
    />
  </div>
);

const StepCard = ({
  number,
  title,
  children,
}: {
  number: number;
  title: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="border border-gray-100 dark:border-neutral-700 rounded-xl p-5 bg-white dark:bg-neutral-800 shadow-sm space-y-3">
    <div className="flex items-center gap-3">
      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-600 text-white text-sm font-bold flex-shrink-0">
        {number}
      </div>
      <h4 className="font-semibold text-gray-900 dark:text-white text-base">{title}</h4>
    </div>
    <div className="pl-11">{children}</div>
  </div>
);

const Callout = ({
  type,
  title,
  children,
}: {
  type: "warning" | "info" | "success" | "tip";
  title?: string;
  children: React.ReactNode;
}) => {
  const styles = {
    warning: {
      wrapper: "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800",
      icon: <Warning size={16} weight="fill" className="text-amber-500 flex-shrink-0 mt-0.5" />,
      title: "text-amber-800 dark:text-amber-300",
      body: "text-amber-700 dark:text-amber-400",
    },
    info: {
      wrapper: "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800",
      icon: <Info size={16} weight="fill" className="text-blue-500 flex-shrink-0 mt-0.5" />,
      title: "text-blue-800 dark:text-blue-300",
      body: "text-blue-700 dark:text-blue-400",
    },
    success: {
      wrapper: "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800",
      icon: <CheckCircle size={16} weight="fill" className="text-green-500 flex-shrink-0 mt-0.5" />,
      title: "text-green-800 dark:text-green-300",
      body: "text-green-700 dark:text-green-400",
    },
    tip: {
      wrapper: "bg-violet-50 dark:bg-violet-900/20 border-violet-200 dark:border-violet-800",
      icon: <Info size={16} weight="fill" className="text-violet-500 flex-shrink-0 mt-0.5" />,
      title: "text-violet-800 dark:text-violet-300",
      body: "text-violet-700 dark:text-violet-400",
    },
  };
  const s = styles[type];
  return (
    <div className={`rounded-lg border p-3 flex gap-2.5 ${s.wrapper}`}>
      {s.icon}
      <div>
        {title && <p className={`text-sm font-semibold mb-0.5 ${s.title}`}>{title}</p>}
        <div className={`text-sm leading-relaxed ${s.body}`}>{children}</div>
      </div>
    </div>
  );
};

const GoogleGuide: React.FC = () => {
  const [fancyboxRef] = useFancybox({});

  return (
    <section
      id="google"
      className="scroll-mt-24 rounded-xl border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-900 p-5 shadow-sm space-y-4"
      ref={fancyboxRef}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-1">
        <div className="size-10 flex items-center justify-center bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 shadow-sm p-1.5 flex-shrink-0">
          <svg width="28" height="28" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
            <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
            <path fill="#FF3D00" d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
            <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
            <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
          </svg>
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">
            Google IAP Integration
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Integrating Google Play Store with AppRevBooster
          </p>
        </div>
      </div>

      {/* Intro */}
      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
        This guide provides the complete, step-by-step process for integrating your{" "}
        <span className="font-semibold">Google Play Store</span> account with AppRevBooster.
        Integrating your store ensures all your in-app purchase and subscription data is
        accurately consolidated for unified reporting and optimization.
      </p>

      {/* Prerequisites */}
      <div className="rounded-xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-4 shadow-sm">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <CheckCircle size={18} weight="duotone" className="text-indigo-500" />
          Before You Begin (Prerequisites)
        </h3>
        <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
          {[
            "You have a Google Cloud project associated with your app.",
            "Your initial setup and integration of your Google Ads and Google AdMob accounts with AppRevBooster are complete.",
            "You will need access to both the Google Cloud Console and the Google Play Console to complete the following steps.",
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="mt-1 w-4 h-4 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                {i + 1}
              </span>
              {item}
            </li>
          ))}
        </ul>
      </div>

      {/* Steps */}
      <div className="space-y-3">

        {/* Step 1 */}
        <StepCard number={1} title="Enable Developer APIs in Google Cloud Console">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            Activating the correct Developer APIs is essential for AppRevBooster to communicate
            seamlessly with your app's data, facilitating real-time data analysis.
          </p>
          <Callout type="info">
            If your app is not distributed via the Play Store, you can skip this step.
          </Callout>
          <ol className="mt-3 list-decimal pl-5 text-sm text-gray-700 dark:text-gray-300 space-y-2">
            <li>
              Open the{" "}
              <RedirectLink href="https://console.cloud.google.com/" text="Google Cloud Console" />{" "}
              and sign in.
            </li>
            <li>
              In the top-left corner, select the Google Cloud Project you wish to use.
              <StepImage step="1" />
            </li>
            <li>
              Enable the following three APIs, one by one:
              <ul className="mt-2 pl-3 list-none space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 mt-0.5">○</span>
                  <span>
                    <RedirectLink
                      href="https://console.cloud.google.com/apis/library/androidpublisher.googleapis.com"
                      text="Google Play Android Developer API"
                    />{" "}
                    — for general app management.
                    <StepImage step="2" />
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 mt-0.5">○</span>
                  <span>
                    <RedirectLink
                      href="https://console.cloud.google.com/apis/library/playdeveloperreporting.googleapis.com"
                      text="Google Play Developer Reporting API"
                    />{" "}
                    — for data and reporting access.
                    <StepImage step="3" />
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 mt-0.5">○</span>
                  <span>
                    <RedirectLink
                      href="https://console.cloud.google.com/marketplace/product/google/pubsub.googleapis.com"
                      text="Cloud Pub/Sub API"
                    />{" "}
                    — to enable real-time developer notifications.
                    <StepImage step="4" />
                  </span>
                </li>
              </ul>
            </li>
            <li>
              <strong>Verification:</strong> Navigate to the APIs &amp; Services page in the Google
              Cloud Console and confirm that all three listed APIs are enabled.
              <StepImage step="5" />
            </li>
          </ol>
        </StepCard>

        {/* Step 2 */}
        <StepCard
          number={2}
          title={
            <RedirectLink
              href="https://console.cloud.google.com/iam-admin/serviceaccounts"
              text="Create a Service Account & Assign Roles"
            />
          }
        >
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            A Service Account is a special Google account used by AppRevBooster to securely
            access your data without requiring user credentials.
          </p>
          <ol className="list-decimal pl-5 text-sm text-gray-700 dark:text-gray-300 space-y-2">
            <li>
              In the Google Cloud Console, navigate to{" "}
              <RedirectLink
                href="https://console.cloud.google.com/iam-admin/serviceaccounts"
                text="IAM & Admin > Service accounts"
              />{" "}
              and click the <strong>Create service account</strong> button.
              <StepImage step="6" />
            </li>
            <li>Click the <strong>Create service account</strong> button.</li>
            <li>
              Enter a descriptive name — we recommend including "AppRevBooster Access" to indicate
              the purpose of this account. The Service account ID is created automatically.
              <StepImage step="7" />
            </li>
            <li>
              Copy and save the full{" "}
              <strong>Service Account Email Address</strong>{" "}
              (located just below the Service Account ID). You will need this email in Step 3.
            </li>
            <li>Click <strong>Create and continue</strong>.</li>
            <li>
              In the <em>Grant this service account access to project</em> section, assign the
              following roles:
              <ul className="mt-2 pl-3 list-none space-y-1.5">
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 mt-0.5">○</span>
                  <span>
                    <strong>Storage Object Viewer</strong> — read-only access to GCS buckets.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 mt-0.5">○</span>
                  <span>
                    <strong>Monitoring Viewer</strong> — enables monitoring of the notification queue.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 mt-0.5">○</span>
                  <span>
                    <strong>Pub/Sub Admin</strong> — enables real-time developer notifications.
                  </span>
                </li>
              </ul>
            </li>
            <li>
              Click <strong>Continue</strong>, then click <strong>Done</strong> without any changes.
              The Service accounts window opens.
              <StepImage step="12" />
            </li>
          </ol>
        </StepCard>

        {/* Step 3 */}
        <StepCard number={3} title="Grant Permissions in Google Play Console">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            You must now grant the Service Account created in Step 2 the necessary access within
            the Google Play Console.
          </p>
          <ol className="list-decimal pl-5 text-sm text-gray-700 dark:text-gray-300 space-y-2">
            <li>
              Open the{" "}
              <RedirectLink
                href="https://play.google.com/console/u/1/developers/6627684772173531579/users-and-permissions"
                text="Google Play Console"
              />{" "}
              and navigate to{" "}
              <RedirectLink
                href="https://play.google.com/console/u/1/developers/6627684772173531579/users-and-permissions"
                text="Users and permissions"
              />
              .
            </li>
            <li>
              Click <strong>Invite new users</strong>.
              <StepImage step="14" />
            </li>
            <li>
              Paste the Service Account Email Address copied in Step 2 (point 4) into the{" "}
              <strong>Email address</strong> field.
              <StepImage step="15" />
            </li>
            <li>Click on the <strong>Account permissions</strong> tab.</li>
            <li>
              Grant the following permissions:
              <ul className="mt-2 pl-3 list-none space-y-1.5">
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 mt-0.5">○</span>
                  <strong>View app information and download bulk reports</strong> (Read-only).
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 mt-0.5">○</span>
                  <strong>View financial data, orders, and cancellation survey responses.</strong>
                </li>
                <StepImage step="15-2" />
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 mt-0.5">○</span>
                  <strong>Manage orders and subscriptions</strong>.
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 mt-0.5">○</span>
                  <strong>Manage store presence</strong>.
                </li>
              </ul>
            </li>
            <li>
              Click <strong>Invite user</strong>, then click <strong>Send invite</strong>. The
              service account will now appear in your user list.
              <StepImage step="16" />
            </li>
          </ol>
        </StepCard>

        {/* Step 4 */}
        <StepCard number={4} title="Generate and Save the Service Account Key (JSON)">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            Integration with AppRevBooster requires a service account key file from the Google
            Cloud Console. This file must be uploaded to AppRevBooster to establish a secure link.
          </p>
          <Callout type="tip" title="Pro-Tip: Expediting Service Account Activation">
            It usually takes at least 24 hours for your new service account to become active.
            To speed this up: after creating the service account, open any application and
            navigate to <strong>Monetize → Products → Subscriptions/In-app products</strong>.
            Edit the description of any product and save — this activates the service account
            immediately. Revert the changes afterward.
          </Callout>
          <ol className="mt-3 list-decimal pl-5 text-sm text-gray-700 dark:text-gray-300 space-y-3">
            <li>
              <RedirectLink
                href="https://console.cloud.google.com/iam-admin/serviceaccounts"
                text="Open Service Accounts & Start Key Creation"
              />
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                Navigate to the Service accounts section in the Google Cloud Console. Verify you
                have selected the correct project. Click <strong>Add key</strong> → <strong>Create new key</strong>.
              </p>
              <StepImage step="17" />
            </li>
            <li>
              Generate and download the key file
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                In the confirmation window, ensure <strong>JSON</strong> is selected, then click{" "}
                <strong>Create</strong>. Your browser will automatically download the key file.
              </p>
              <StepImage step="18" />
            </li>
            <li>
              Locate and secure the key file
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                The key is saved as a JSON file (e.g.,{" "}
                <code className="bg-gray-100 dark:bg-neutral-700 px-1 rounded text-xs">
                  [project-name]-[unique-id].json
                </code>
                ) on your computer.
              </p>
              <StepImage step="19" />
            </li>
          </ol>
        </StepCard>

        {/* Step 5 */}
        <StepCard
          number={5}
          title={
            <RedirectLink
              href="https://play.google.com/console/u/1/developers"
              text="Get the Financial Reports Bucket Name"
            />
          }
        >
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            You need the <strong>Cloud Storage URI (Bucket name)</strong> where your financial
            reports are stored to enable AppRevBooster to fetch historical revenue data.
          </p>
          <ol className="list-decimal pl-5 text-sm text-gray-700 dark:text-gray-300 space-y-2">
            <li>
              In the Google Play Console, scroll down the left navigation bar to the{" "}
              <RedirectLink
                href="https://play.google.com/console/u/1/developers"
                text="Download Reports"
              />{" "}
              section.
            </li>
            <li>Click on <strong>Financial</strong>.</li>
            <li>
              In the Download Financial Reports section, look for the Cloud Storage URI link.
            </li>
            <li>
              <div className="flex items-center gap-1.5 flex-wrap">
                Click <Copy size={14} className="mx-1" />
                <strong>Copy Cloud Storage URI</strong>. This is your{" "}
                <strong>Bucket Name</strong>.
              </div>
              <StepImage step="21" />
            </li>
          </ol>
        </StepCard>

        {/* Step 6 */}
        <StepCard number={6} title="Complete Integration in AppRevBooster">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            You now have the two key items needed to finish the integration:
          </p>
          <ul className="list-disc pl-5 text-sm text-gray-700 dark:text-gray-300 space-y-1 mb-3">
            <li>
              The <strong>Service Account JSON Key File</strong> (from Step 4).
            </li>
            <li>
              The <strong>Bucket Name</strong> / Cloud Storage URI (from Step 5).
            </li>
          </ul>
          <ol className="list-decimal pl-5 text-sm text-gray-700 dark:text-gray-300 space-y-1.5">
            <li>Log in to your AppRevBooster account.</li>
            <li>Navigate to the integration or Add Account section for Google IAP.</li>
            <li>Upload the <strong>JSON Key File</strong>.</li>
            <li>
              Paste the copied <strong>Bucket Name</strong> into the corresponding field.
            </li>
            <li>Complete the final steps in the AppRevBooster interface.</li>
          </ol>
          <StepImage step="22" className="!w-auto h-[500px]" />
        </StepCard>
      </div>

      {/* Completion banner */}
      <div className="rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-4 flex gap-3">
        <ShieldCheck size={20} weight="fill" className="text-green-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-green-800 dark:text-green-300">
            Integration Complete!
          </p>
          <p className="mt-1 text-sm text-green-700 dark:text-green-400">
            Your Google Play Store account is now integrated with AppRevBooster. You can begin
            managing and analyzing your in-app purchase and subscription data seamlessly from
            your dashboard.
          </p>
        </div>
      </div>
    </section>
  );
};

export default GoogleGuide;
